import type { MonopolyPayload } from '../report';
import { fmtDecimal, fmtInt, fmtPct, niceMax, share } from '../utils';
import { Bars, GroupedBars, HBars, KpiTile, Panel, Stat, StackedBars, Swatch } from './Primitives';

/* The four detail tabs. Each is a straight transcription of its design tab. */

export function ParticipantsTab({ data }: { data: MonopolyPayload }) {
  const { participants: p, days } = data;
  const funnelFills = ['fill-blue-dark', 'fill-blue', 'fill-cyan', 'fill-red'];
  const channelFill = (label: string) => (/kyc/i.test(label) ? 'fill-red' : /newsletter/i.test(label) ? 'fill-blue' : 'fill-cyan');
  return (
    <>
      <div className="mono-grid-4">
        <KpiTile label="Participants uniques" value={fmtInt(p.unique)} />
        <KpiTile label="Opt-ins (CGU)" value={fmtInt(p.optIns)} />
        {/* Consents, not people: the headline figure matches the sum of the
            channel list below. The member count rides along as the sub-line so
            both are on screen and neither can be mistaken for the other. */}
        <KpiTile label="Consentements collectés" value={fmtInt(p.consentsGranted ?? p.marketingConsents)} sub={`${fmtInt(p.marketingConsents)} participants`} />
        <KpiTile label="Participants récurrents" value={fmtPct(p.returningPct)} />
      </div>
      <Panel title="Nouveaux participants par jour">
        <Bars
          values={p.newByDay}
          days={days}
          max={niceMax(p.newByDay)}
          height={240}
          highlightLast
          valueLabels
          loose
          dayStyle="long"
          seriesLabel="Nouveaux participants"
          note="Comptes créés ce jour-là (première authentification dans l'app)"
          ariaLabel="Nouveaux participants par jour"
        />
      </Panel>
      <div className="mono-grid-2">
        <Panel title="Parcours d'inscription" tight>
          <div className="mono-funnel">
            {p.funnel.map((s, i) => (
              <div key={s.label} className="mono-funnel-step">
                <div className="mono-row-between">
                  <span>{s.label}</span>
                  <span style={{ fontWeight: 700 }}>{fmtInt(s.value)}</span>
                </div>
                <div className="track">
                  <span className={funnelFills[i % funnelFills.length]} style={{ width: `${Math.min(100, s.pct)}%` }} />
                </div>
              </div>
            ))}
          </div>
        </Panel>
        <Panel
          title="Canaux de consentement"
          action={p.consentsGranted !== undefined ? <span className="mono-note">Total : {fmtInt(p.consentsGranted)} consentements accordés</span> : undefined}
          tight
        >
          <HBars rows={p.consentChannels.map((c) => ({ label: c.label, pct: c.pct, value: fmtInt(c.value), fill: channelFill(c.label) }))} />
          {/* The total counts consents, not people. Said in the open rather than
              on hover — this figure has already been read the other way. */}
          <p className="mono-foot">Chaque consentement enregistré compte : un participant qui accepte trois canaux apparaît sur les trois lignes.</p>
        </Panel>
      </div>
      {/* Consents per day. Rendered only when the pipeline publishes the series:
          a payload without it gets no chart rather than a flat row of zeros,
          which would read as "nobody consented". */}
      {p.consentsByDay && (
        <Panel
          title="Consentements par jour"
          action={
            <span className="mono-note">
              {fmtInt(Math.round(p.consentsByDay.reduce((a, v) => a + v, 0) / Math.max(1, p.consentsByDay.length)))} par jour en moyenne · pic{' '}
              {fmtInt(Math.max(...p.consentsByDay))}
            </span>
          }
        >
          <Bars
            values={p.consentsByDay}
            days={days}
            max={niceMax(p.consentsByDay)}
            height={240}
            highlightLast
            valueLabels
            loose
            dayStyle="long"
            tone="cyan"
            seriesLabel="Consentements collectés"
            cumulative
            note="Consentements enregistrés ce jour-là, tous canaux confondus"
            ariaLabel="Consentements collectés par jour"
          />
        </Panel>
      )}
    </>
  );
}

export function VisitsTab({ data }: { data: MonopolyPayload }) {
  const { visits: v, days } = data;
  const sourceFills = ['fill-blue-dark', 'fill-blue', 'fill-cyan', 'fill-red'];
  const maxSource = Math.max(...(v.sources ?? []).map((s) => s.pct), 1);
  const hasBreakdown = Boolean(v.sources || v.topScreens);
  return (
    <>
      <div className="mono-grid-4">
        <KpiTile label="Visites totales" value={fmtInt(v.total)} />
        <KpiTile label="Visiteurs uniques" value={fmtInt(v.uniqueVisitors)} />
        <KpiTile label="Sessions / visiteur" value={fmtDecimal(v.sessionsPerVisitor)} />
        <KpiTile label="Session moyenne" value={v.avgSession ?? '—'} sub={v.avgSession ? undefined : "nécessite le flux d'événements analytics"} />
      </div>
      <Panel title="Visites par jour">
        <Bars
          values={v.byDay}
          days={days}
          max={niceMax(v.byDay)}
          height={300}
          tone="cyan"
          highlightLast
          valueLabels
          loose
          dayStyle="long"
          seriesLabel="Visites"
          note="Une connexion compte comme visite s'il n'y a pas eu de connexion dans les 30 minutes précédentes"
          ariaLabel="Visites par jour"
        />
      </Panel>
      {hasBreakdown && (
        <div className="mono-grid-2">
          {v.sources && (
            <Panel title="Origine des visites" tight>
              <HBars
                rows={v.sources.map((s, i) => ({
                  label: s.label,
                  pct: (s.pct / maxSource) * 100,
                  value: fmtPct(s.pct, 0),
                  fill: sourceFills[i % sourceFills.length],
                }))}
                wideLabel
                narrowValue
              />
            </Panel>
          )}
          {v.topScreens && (
            <Panel title="Écrans les plus consultés" tight>
              <div className="mono-ranked">
                {v.topScreens.map((s) => (
                  <div key={s.label}>
                    <span>{s.label}</span>
                    <span className="v">{fmtInt(s.value)}</span>
                  </div>
                ))}
              </div>
            </Panel>
          )}
        </div>
      )}
    </>
  );
}

export function CardsTab({ data }: { data: MonopolyPayload }) {
  const { cards: c, overview: o, days } = data;
  const usedPct = share(o.cardsUsed.total, c.printed);
  const channelFills = ['fill-blue', 'fill-cyan', 'fill-red'];
  const maxPacks = Math.max(...c.channels.map((ch) => ch.packs), 1);
  return (
    <>
      <div className="mono-grid-4">
        <KpiTile
          label="Cartes imprimées"
          value={fmtInt(c.printed)}
          sub={
            c.printedPhysical !== undefined && c.printedDigital !== undefined
              ? `${compactM(c.printedPhysical)} physiques · ${compactM(c.printedDigital)} digitales`
              : c.printedPhysical !== undefined
                ? 'cartes physiques · le digital est distribué par commande'
                : 'tous canaux'
          }
        />
        <KpiTile label="Cartes utilisées" value={fmtInt(o.cardsUsed.total)} sub={`${fmtPct(usedPct)} du tirage`} valueTone="blue" />
        <KpiTile label="Packs digitaux distribués" value={fmtInt(c.digitalGranted)} sub={`${fmtDecimal(c.avgPacksPerOrder, 1)} en moyenne par commande`} />
        <KpiTile label="Codes en erreur" value={fmtInt(c.codeErrors)} sub={`${fmtInt(c.usersBlocked)} utilisateurs bloqués 24 h`} tone="alert" />
      </div>

      <Panel title="Cartes utilisées par jour — physiques et digitales">
        <StackedBars
          values={c.byDay}
          days={days}
          max={niceMax(c.byDay.map((d) => d.physical + d.digital))}
          height={260}
          labels={{ physical: 'Cartes physiques', digital: 'Cartes digitales', total: 'Total' }}
          note="Codes validés dans l'app : carte physique saisie, ou carte issue d'un pack digital"
          ariaLabel="Cartes utilisées par jour, physiques et digitales"
        />
        <div className="mono-legend">
          <div className="items">
            <span>
              <Swatch fill="blue" />
              Codes de cartes physiques
            </span>
            <span>
              <Swatch fill="cyan" />
              Cartes digitales
            </span>
          </div>
        </div>
      </Panel>

      {c.streets && (
        <Panel title="Cartes de rue collectées" tight>
          <div className="mono-grid-3">
            <Stat
              label="Cartes de rue"
              value={fmtInt(c.streets.claimed)}
              sub={`${fmtPct(share(c.streets.claimed, o.cardsUsed.total))} des cartes utilisées · doublons compris`}
              tone="blue"
            />
            <Stat label="Rues distinctes" value={`${fmtInt(c.streets.distinct)} / ${fmtInt(c.streets.total)}`} sub="rues du plateau déjà distribuées" />
            <Stat label="Cartes rares" value={fmtInt(c.streets.rare)} sub="la carte qui débloque chaque collection" tone="red" />
          </div>
        </Panel>
      )}

      {c.diceByDay && o.dice && (
        <Panel title="Lancer de dés — lancers et tours par jour">
          <GroupedBars
            values={c.diceByDay.map((d) => ({ a: d.rolled, b: d.laps }))}
            days={days}
            max={niceMax(c.diceByDay.flatMap((d) => [d.rolled, d.laps]))}
            height={200}
            labels={{ a: 'Dés lancés', b: 'Tours terminés' }}
            note="1 lancer pour 3 cartes utilisées · un tour complet = 40 cases"
            ariaLabel="Lancers de dés et tours de plateau par jour"
          />
          <div className="mono-legend">
            <div className="items">
              <span>
                <Swatch fill="blue" />
                {o.dice.rolled !== undefined ? `Dés lancés — ${fmtInt(o.dice.rolled)} au total` : `Cases parcourues — ${fmtInt(o.dice.steps ?? 0)} au total`}
              </span>
              {o.dice.boardsCompleted !== undefined && (
                <span>
                  <Swatch fill="red" />
                  Tours terminés — {fmtInt(o.dice.boardsCompleted)} au total
                </span>
              )}
            </div>
          </div>
        </Panel>
      )}

      <Panel title="Packs digitaux par canal e-commerce" tight>
        <div className="mono-grid-3">
          {c.channels.map((ch, i) => (
            <div key={ch.label} className="mono-channel">
              <div className="mono-channel-head">
                <span className="name">{ch.label}</span>
                <span className="min">{ch.minOrder}</span>
              </div>
              <div className="mono-channel-value">{fmtInt(ch.packs)}</div>
              <div className="mono-bar thin">
                <span className={channelFills[i % channelFills.length]} style={{ width: `${(ch.packs / maxPacks) * 100}%` }} />
              </div>
              <div className="mono-note">
                {fmtPct(ch.sharePct)} des packs digitaux{ch.perOrder !== undefined ? ` · ${fmtDecimal(ch.perOrder, 1)} par commande` : ''}
              </div>
            </div>
          ))}
        </div>
      </Panel>
    </>
  );
}

export function PrizesTab({ data }: { data: MonopolyPayload }) {
  const { prizes: z } = data;
  // The rare-card column only appears once the payload carries the figures.
  const hasRare = z.collections.some((c) => c.rareClaimed !== undefined);
  return (
    <>
      <div className="mono-grid-3">
        <KpiTile label="Gains instantanés réclamés" value={fmtInt(z.instantWins)} sub={`sur ${fmtInt(z.instantWinsTotal)} lots`} />
        <KpiTile label="Collections complétées" value={fmtInt(z.collectionsCompleted)} sub={`sur ${z.collectionGroups} groupes`} />
        <KpiTile label="Cartes Chance" value={fmtInt(z.chanceCards)} sub={z.chanceNote} />
      </div>

      <Panel title="Collections de rues — lots gagnés et stock" tight>
        <table className="mono-table">
          <thead>
            <tr>
              <th>Collection</th>
              <th>Lot</th>
              {hasRare && <th className="num">Cartes rares réclamées</th>}
              <th className="num">Stock</th>
              <th className="num">Gagnés</th>
              <th className="num">% réclamé</th>
            </tr>
          </thead>
          <tbody>
            {z.collections.map((row) => (
              <tr key={row.name}>
                <td>
                  <span className="coll">
                    <span className="coll-swatch" style={{ background: row.color }} aria-hidden="true" />
                    {row.name}
                  </span>
                  {row.rare && <div className="mono-coll-rare">carte rare : {row.rare}</div>}
                </td>
                <td>{row.prize}</td>
                {hasRare && (
                  <td className="num strong" title="Chaque collection est bloquée par cette carte : personne ne peut la compléter sans elle.">
                    {fmtInt(row.rareClaimed ?? 0)}
                  </td>
                )}
                <td className="num muted">{fmtInt(row.stock)}</td>
                <td className="num strong">{fmtInt(row.won)}</td>
                <td className="num">{fmtPct(share(row.won, row.stock), 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>

      <Panel title="Répartition des gains instantanés" tight>
        <div className="mono-grid-3">
          {z.instantWinMix.map((m) => (
            <Stat key={m.label} label={m.label} value={fmtInt(m.value)} sub={`${fmtPct(m.pct)} des gains instantanés`} tone="md" />
          ))}
        </div>
      </Panel>
    </>
  );
}

/** 62_000_000 → "62 M", 8_000_000 → "8 M" — the printed-cards footnote. */
function compactM(n: number): string {
  return n >= 1_000_000 ? `${fmtDecimal(n / 1_000_000, 1)}\u00a0M` : fmtInt(n);
}
